/// <reference types="vite/client" />
import { z } from 'zod';

/**
 * Helper to convert empty/undefined values to undefined for proper default handling
 * Zod .default() only works when value is undefined, not when it's an empty string
 */
const withDefault = (defaultValue: string) =>
  z.preprocess(
    (val) => (val === '' || val === undefined ? undefined : val),
    z.string().default(defaultValue)
  );

const defaultApiBaseUrl = import.meta.env.DEV
  ? 'http://localhost:3000/api/v1'
  : 'https://dev.erp.api.routeegypt.com/api/v1';

/**
 * Environment variables schema
 * Add all required environment variables here
 */
const envSchema = z
  .object({
    // Shared API Configuration (defaults for all portals)
    VITE_API_BASE_URL: withDefault(defaultApiBaseUrl),
    VITE_API_TIMEOUT: z.coerce.number().default(30000),
    VITE_UPLOADS_BASE_URL: withDefault('https://pub-60383362670641e7ac5bc38e81636bc8.r2.dev'),

    // App Configuration
    VITE_APP_NAME: z.string().optional(),
    VITE_APP_ENV: z.enum(['development', 'staging', 'production']).optional(),

    // Feature Flags
    VITE_ENABLE_ANALYTICS: z.coerce.boolean().optional(),
    VITE_ENABLE_DEBUG: z.coerce.boolean().optional(),
  })
  .passthrough(); // Allow other env vars to pass through

/**
 * Validated and type-safe environment variables
 * Use this instead of import.meta.env to ensure type safety
 */
export const env = (() => {
  try {
    return envSchema.parse(import.meta.env);
  } catch (error) {
    console.error('❌ Invalid environment variables:');
    if (error instanceof z.ZodError) {
      error.issues.forEach((err) => {
        console.error(`  - ${err.path.join('.')}: ${err.message}`);
      });
    }
    throw new Error('Environment validation failed. Please check your .env file.');
  }
})();

/**
 * Type for environment variables
 */
export type Env = z.infer<typeof envSchema>;

/**
 * Check if we're in development mode
 */
export const isDevelopment = env.VITE_APP_ENV === 'development' || !env.VITE_APP_ENV;

/**
 * Check if we're in production mode
 */
export const isProduction = env.VITE_APP_ENV === 'production';

/**
 * Check if we're in staging mode
 */
export const isStaging = env.VITE_APP_ENV === 'staging';
